import React, { useRef, useState } from 'react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Input } from '@nextui-org/react';
import { ID_CONSTANTS } from '../utils/constants';
import { SpeechUtility } from '../utils/speechUtils';

interface SearchableDropdownProps {
  onSelectionChange: (key: string) => void;
  className?: string;
  selectedKey: string;
  availableLanguages?: { [key: string]: string };
  setLanguageOnSelection?: boolean;
  closeOnSelect?: boolean;
  searchPlaceholder?: string;
  ariaLabel?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  className,
  onSelectionChange,
  selectedKey,
  availableLanguages = SpeechUtility.languages,
  setLanguageOnSelection = true,
  closeOnSelect = true,
  searchPlaceholder = 'Search language...',
  ariaLabel = 'Single selection example',
}) => {

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const languageKeys = Object.entries(availableLanguages);

  const filteredItems = languageKeys.filter(([key, value]) =>
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedValue = React.useMemo(() => {
    if (availableLanguages[selectedKey]) return availableLanguages[selectedKey];
    const firstEntry = Object.entries(availableLanguages)[0];
    return firstEntry?.[1] ?? selectedKey;
  }, [availableLanguages, selectedKey]);
  return (
    <Dropdown
      className={className}
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      }}
    >
      <DropdownTrigger>
        <Button
          id={ID_CONSTANTS.chatSpeechLanguageBtn + selectedKey}
          size='sm'
          variant="flat"
          className="capitalize dark:border dark:border-[color:var(--cty-field-border)]"
        >
          {selectedValue}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Single selection example"
        variant="flat"
        disallowEmptySelection
        selectionMode="single"
        closeOnSelect={closeOnSelect}
        selectedKeys={
          availableLanguages[selectedKey] ? selectedKey : Object.keys(availableLanguages)[0]
        }
        onSelectionChange={(selkey) => {
          const key: string = Array.from(selkey)[0].toString();
          onSelectionChange(key);
          if (setLanguageOnSelection) {
            SpeechUtility.setLanguage(key);
          }
          if (closeOnSelect) {
            setIsOpen(false);
          }
        }}
        defaultSelectedKeys={[selectedKey]}
        className='w-[200px] overflow-y-auto'
      >
        <DropdownItem key="search" isReadOnly>
          <Input
            ref={inputRef}
            id="language-search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onKeyPress={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          />
        </DropdownItem >
        <>
          {filteredItems.map(([key, value]) => {
            return <DropdownItem key={key}>{value}</DropdownItem>;
          })}
        </>
      </DropdownMenu>
    </Dropdown>
  );
};

export { SearchableDropdown };
